<script setup lang="ts">
import { computed, toRef } from 'vue'
import { RouterLink } from 'vue-router'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import { useAgentSessionDevcontainerInfo } from '../composables/useAgentSessionDevcontainerInfo'
import { agentCostLabel } from './AgentCostLabel'
import { sessionRepo } from './AgentSessionGroups'
import type { AgentSessionSummary } from '../stores/agent/types'

// The right-hand Workspace panel on /agent: facts about the open session's
// workspace that the brain already knows — its repo, running cost, last
// activity, and the box-side devcontainer detail (#1501: slot, workspace
// path, whether the container is up). Per-repo metrics live on the repo's
// own row on the Repos page, linked below, not here (a repo's devcontainer
// can be shared across sessions, so they aren't this session's numbers).
const props = defineProps<{
  threadId: string
  session: AgentSessionSummary | null
}>()

const { info, loading } = useAgentSessionDevcontainerInfo(toRef(props, 'threadId'))
const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()

const repo = computed(() => (props.session ? sessionRepo(props.session) : 'superbrain'))

interface Fact {
  key: string
  label: string
  value: string
  title?: string
  mono?: boolean
}

const facts = computed<Fact[]>(() => {
  const rows: Fact[] = [{ key: 'repo', label: 'Repo', value: repo.value, mono: true }]
  const s = props.session
  if (s?.total_cost_cents != null)
    rows.push({ key: 'cost', label: 'Cost so far', value: agentCostLabel(s.total_cost_cents) })
  if (s?.last_activity_at)
    rows.push({
      key: 'activity',
      label: 'Last activity',
      value: formatDate(s.last_activity_at, now.value),
      title: formatAbsoluteDate(s.last_activity_at),
    })
  const box = info.value
  if (box) {
    rows.push({ key: 'container', label: 'Container', value: box.container_stopped_at ? 'Stopped' : 'Up' })
    rows.push({ key: 'slot', label: 'Slot', value: box.slot == null ? '—' : String(box.slot), mono: true })
    rows.push({ key: 'workspace', label: 'Workspace', value: box.workspace, mono: true })
  }
  return rows
})
</script>

<template>
  <aside
    aria-label="Workspace"
    class="flex w-[300px] shrink-0 flex-col gap-3 overflow-y-auto border-l border-border-divider p-5"
    data-testid="agent-workspace-panel"
  >
    <h2 class="m-0 text-[11px] font-semibold tracking-[0.08em] text-ink-faint uppercase">Workspace</h2>
    <dl class="m-0">
      <div
        v-for="fact in facts"
        :key="fact.key"
        class="flex min-h-10 items-center gap-2.5 border-t border-border-divider py-2"
        :data-testid="`agent-workspace-${fact.key}`"
      >
        <dt class="flex-1 text-sm text-ink-muted">{{ fact.label }}</dt>
        <dd
          class="m-0 min-w-0 text-right break-all text-ink"
          :class="fact.mono ? 'font-mono text-xs' : 'text-sm tabular-nums'"
          :title="fact.title"
        >
          {{ fact.value }}
        </dd>
      </div>
    </dl>
    <p v-if="!info && !loading" class="m-0 text-[13px] text-ink-faint" data-testid="agent-workspace-no-container">
      No devcontainer on the agent box for this session right now.
    </p>
    <RouterLink
      :to="{ name: 'adminAgentRepos', query: { repo } }"
      class="text-[13px] text-factory underline"
      data-testid="agent-workspace-repo-link"
      >Repo settings</RouterLink
    >
  </aside>
</template>
